import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { CONSENT_AGENT_ID } from './consent_agent.types';
import { isSpreadsheetFile } from '../utils/consent_upload_file.helpers';
export { isSpreadsheetFile };
function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = typeof reader.result === 'string' ? reader.result.split(',')[1] : '';
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
function getResultFromAgent(events) {
    return events.flatMap((event) => (event.content?.parts ?? []).flatMap((part) => {
        if (!part.text) {
            return [];
        }
        try {
            return JSON.parse(part.text);
        }
        catch {
            return part.text;
        }
    }));
}
export function useConsentAgent(sites) {
    const agentStudioClient = useApiClient(ApiClientService.AGENT_STUDIO_API);
    const sessionIdRef = useRef(uuidv4());
    const sendMessageToAgent = useCallback(async (body) => {
        const response = await agentStudioClient.post('sessions/messages', {
            agent_id: CONSENT_AGENT_ID,
            session_id: sessionIdRef.current,
            ...body,
        });
        const apiResponse = getResultFromAgent(response.data.events);
        const sessionId = response.headers['x-session-id'];
        if (sessionId) {
            sessionIdRef.current = sessionId;
        }
        return apiResponse.findLast((result) => typeof result === 'object' && result !== null && 'icfs' in result);
    }, [agentStudioClient]);
    const processFiles = useCallback(async (files) => {
        if (files.length === 0) {
            return undefined;
        }
        const spreadsheets = files.filter(isSpreadsheetFile);
        const pdfDocxFiles = files.filter((f) => !isSpreadsheetFile(f));
        const pdfDocxFilenames = pdfDocxFiles.map((f) => f.name);
        const spreadsheetFiles = await Promise.all(spreadsheets.map(async (file) => ({ fileName: file.name, fileContent: await readFileAsBase64(file) })));
        const hasFilesForAgent = pdfDocxFilenames.length > 0 || spreadsheetFiles.length > 0;
        if (!hasFilesForAgent) {
            return undefined;
        }
        // Send sites context together with the file data so the agent has the
        // full picture in one call, avoiding an eager request on page load.
        const messages = [];
        if (sites && sites.length > 0) {
            messages.push({ type: 'text', text: JSON.stringify(sites) });
        }
        messages.push({
            type: 'text',
            text: JSON.stringify({ pdfDocxFilenames, spreadsheetFiles }),
        });
        return sendMessageToAgent({ messages });
    }, [sites, sendMessageToAgent]);
    return { processFiles };
}
